import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, CheckCircle2, CircleDashed, Eye, Layers3, Ruler, Settings2 } from 'lucide-react';
import { focarObjeto } from '../../../../lib/corelOperations';
import { curveToPolylines } from '../../../../lib/montatech/utils/bezier';
import { coletarObjetos, escanearPagina, finalizarESalvarMoldeAtual, lerPeca, limparPeca, marcarPeca, salvarDadosMolde } from '../services';
import type {
  EscanearPaginaResponse,
  LerPecaResponse,
  MoldeCollectResponse,
  MoldeMetadata,
  MoldeObjectCollectItem,
  MoldePageCandidate,
} from '../types';
import { buildMoldePayload, getCandidateLabel, type MoldeV3UiState } from './catalog';
import { ConfigSheet } from './ConfigSheet';
import { SizeSheet } from './SizeSheet';

type InventorySource = 'coleta' | 'pagina';
type InventoryItem = MoldePageCandidate | MoldeObjectCollectItem;

const EMPTY_COLLECT: MoldeCollectResponse = {
  documento: {
    nome: '',
    caminho: '',
    salvoNoDisco: false,
  },
  pagina: {
    nome: '',
    indice: 0,
  },
  resumo: {
    total: 0,
    mapeados: 0,
    pendentes: 0,
    placeholders: 0,
    conflitos: 0,
  },
  molde: {
    familiaTamanho: '',
    tamanho: '',
    custom: false,
    paginaCanonica: '',
    sincronizadoComPagina: false,
  },
  objetos: [],
  warnings: [],
};

const EMPTY_SCAN: EscanearPaginaResponse = {
  pagina: {
    nome: '',
    indice: 0,
    documentoNome: '',
    documentoCaminho: '',
    salvoNoDisco: false,
  },
  resumo: {
    totalCandidatos: 0,
    mapeados: 0,
    pendentes: 0,
    placeholders: 0,
    conflitos: 0,
  },
  molde: {
    familiaTamanho: '',
    tamanho: '',
    custom: false,
    paginaCanonica: '',
    sincronizadoComPagina: false,
  },
  checklist: [],
  candidatos: [],
  warnings: [],
};

function getStateMeta(state: MoldePageCandidate['state']) {
  switch (state) {
    case 'mapeado':
      return {
        label: 'Mapeado',
        className: 'border-green-700/30 bg-green-900/10 text-green-200',
        icon: CheckCircle2,
      };
    case 'conflito':
      return {
        label: 'Conflito',
        className: 'border-red-700/30 bg-red-900/10 text-red-200',
        icon: AlertTriangle,
      };
    default:
      return {
        label: 'Pendente',
        className: 'border-yellow-700/30 bg-yellow-900/10 text-yellow-200',
        icon: CircleDashed,
      };
    }
}

function getNextPendingId(items: InventoryItem[], currentId: number) {
  if (items.length === 0) return null;
  const currentIndex = items.findIndex((item) => item.staticId === currentId);
  if (currentIndex === -1) return null;

  for (let offset = 1; offset <= items.length; offset += 1) {
    const candidate = items[(currentIndex + offset) % items.length];
    if (candidate.state === 'nao_mapeado') return candidate.staticId;
  }

  return null;
}

function buildSelectionInfo(readInfo: LerPecaResponse) {
  if (readInfo.marcado) return `Shape ativo: ${readInfo.identificador}`;
  if (readInfo.motivo === 'multiplos_shapes_selecionados') return 'Ha mais de 1 shape ativo no CorelDRAW.';
  if (readInfo.motivo === 'nenhum_shape_selecionado') return 'Nenhum shape ativo no CorelDRAW.';
  return 'O shape ativo ainda nao possui marcacao MoldeOne.';
}

function getItemPrimaryLabel(item: InventoryItem) {
  return item.shapeName || getCandidateLabel(item);
}

function getItemMappingLabel(item: InventoryItem) {
  const label = getCandidateLabel(item);
  if (!label || label === item.shapeName || label === 'Objeto sem mapeamento') return null;
  return label;
}

function isEditableStructuralItem(item: InventoryItem) {
  if (item.isText) return false;
  if (!item.peca) return true;
  if (item.peca === 'texto') return false;
  if (item.peca === 'emblema') return false;
  if (item.peca === 'background' || item.peca === 'vetor') return false;
  if (item.peca === 'extra' && item.parte === 'etiqueta') return false;
  return true;
}

function isCollectedItem(item: InventoryItem): item is MoldeObjectCollectItem {
  return 'previewBox' in item && typeof item.previewBox?.aspectRatio === 'number';
}

function getPreviewRectStyle(item: InventoryItem) {
  const preview = isCollectedItem(item) ? item.previewBox : null;
  const aspectRatio = preview?.aspectRatio && preview.aspectRatio > 0 ? preview.aspectRatio : 1;

  if (aspectRatio >= 1) {
    const heightPercent = Math.max(24, Math.min(78, 78 / aspectRatio));
    return { width: '78%', height: `${heightPercent}%` };
  }

  const widthPercent = Math.max(24, Math.min(78, 78 * aspectRatio));
  return { width: `${widthPercent}%`, height: '78%' };
}

function formatMm(value: number | undefined) {
  if (!value || value <= 0) return '--';
  return value.toFixed(1);
}

function getMoldeMetadataLabel(molde?: MoldeMetadata) {
  return molde?.tamanho?.trim() ? molde.tamanho : 'nao definido';
}

function getMoldeSyncLabel(molde?: MoldeMetadata) {
  if (!molde?.tamanho?.trim()) return 'Tamanho pendente';
  return molde.sincronizadoComPagina ? 'Sincronizado com a pagina' : 'Pagina diferente do tamanho';
}

function getPreviewCaption(item: InventoryItem) {
  if (!isCollectedItem(item)) return null;
  if (item.widthMm <= 0 || item.heightMm <= 0) return null;
  return `${formatMm(item.widthMm)} x ${formatMm(item.heightMm)} mm`;
}

function mergeCollectedWorkset(
  collectedItems: MoldeObjectCollectItem[],
  scannedItems: MoldePageCandidate[]
): MoldeObjectCollectItem[] {
  const scannedById = new Map(scannedItems.map((item) => [item.staticId, item]));

  return collectedItems
    .map((item) => {
      const scanned = scannedById.get(item.staticId);
      if (!scanned) return null;

      return {
        ...item,
        shapeName: scanned.shapeName || item.shapeName,
        shapeType: scanned.shapeType || item.shapeType,
        layerName: scanned.layerName || item.layerName,
        isText: scanned.isText,
        state: scanned.state,
        identificador: scanned.identificador,
        peca: scanned.peca,
        parte: scanned.parte,
        lado: scanned.lado,
        index: scanned.index,
        escala: scanned.escala,
        placeholder: scanned.placeholder,
        problems: scanned.problems ?? [],
      };
    })
    .filter((item): item is MoldeObjectCollectItem => Boolean(item));
}

function isLineSegment(
  current: { snp: { x: number; y: number }; scp: { x: number; y: number }; ecp: { x: number; y: number } },
  end: { x: number; y: number }
) {
  return (
    current.scp.x === current.snp.x &&
    current.scp.y === current.snp.y &&
    current.ecp.x === current.snp.x &&
    current.ecp.y === current.snp.y &&
    !(end.x === current.snp.x && end.y === current.snp.y)
  );
}

function getPreviewGeometry(item: MoldeObjectCollectItem) {
  if (!item.previewCurve?.paths?.length) return null;

  const curve = {
    index: 1,
    width: item.previewCurve.width,
    height: item.previewCurve.height,
    maxLength: item.previewCurve.maxLength,
    maxLengthScaled: item.previewCurve.maxLength * 100000000000,
    paths: item.previewCurve.paths.map((path) => ({
      segmentCount: path.segments.length,
      closed: path.closed,
      segments: path.segments.map((segment) => ({
        SNPx: segment.snp.x,
        SNPy: segment.snp.y,
        SCPx: segment.scp.x,
        SCPy: segment.scp.y,
        ECPx: segment.ecp.x,
        ECPy: segment.ecp.y,
      })),
    })),
  };

  const polylines = curveToPolylines(curve);
  if (!polylines.length) return null;

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  polylines.forEach((polyline) => {
    polyline.forEach((point) => {
      if (point.x < minX) minX = point.x;
      if (point.y < minY) minY = point.y;
      if (point.x > maxX) maxX = point.x;
      if (point.y > maxY) maxY = point.y;
    });
  });

  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
    return null;
  }

  const width = Math.max(maxX - minX, 0.001);
  const height = Math.max(maxY - minY, 0.001);

  const transformedPolylines = polylines.map((polyline) =>
    polyline.map((point) => ({
      x: point.x - minX,
      // Mesmo padrao visual do MontaTech v2: Y invertido para manter a peca "em pe".
      y: maxY - point.y,
    }))
  );

  return {
    polylines: transformedPolylines.map((polyline) => polyline.map((point) => `${point.x},${point.y}`).join(' ')),
    viewBox: `0 0 ${width} ${height}`,
    strokeWidth: Math.max(Math.max(width, height) * 0.012, 0.18),
  };
}

function PreviewShapeCard({ item }: { item: InventoryItem }) {
  const previewCaption = getPreviewCaption(item);

  if (!isCollectedItem(item)) {
    return (
      <div className="flex min-w-[84px] shrink-0 flex-col items-start gap-1.5">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-navy-700 bg-navy-950/70 p-2">
          <div className="relative h-full w-full overflow-hidden rounded-md bg-navy-900/40">
            <div
              className="absolute left-1/2 top-1/2 rounded-sm border border-cyan-400/80 bg-cyan-400/5"
              style={{
                ...getPreviewRectStyle(item),
                transform: 'translate(-50%, -50%)',
              }}
            />
          </div>
        </div>
        <div className="min-w-0">
          {previewCaption && <div className="text-[10px] text-navy-300">{previewCaption}</div>}
        </div>
      </div>
    );
  }

  const previewGeometry = getPreviewGeometry(item);

  return (
    <div className="flex min-w-[84px] shrink-0 flex-col items-start gap-1.5">
      <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-navy-700 bg-navy-950/70 p-2">
        <div className="relative h-full w-full overflow-hidden rounded-md bg-navy-900/40">
          {previewGeometry ? (
            <svg viewBox={previewGeometry.viewBox} className="h-full w-full" preserveAspectRatio="xMidYMid meet">
              {previewGeometry.polylines.map((points, index) => (
                <polyline
                  key={`${item.staticId}-poly-${index}`}
                  points={points}
                  fill="none"
                  stroke="rgba(34,211,238,0.95)"
                  strokeWidth={previewGeometry.strokeWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </svg>
          ) : (
            <div
              className="absolute left-1/2 top-1/2 rounded-sm border border-cyan-400/80 bg-cyan-400/5"
              style={{
                ...getPreviewRectStyle(item),
                transform: 'translate(-50%, -50%)',
              }}
            />
          )}
        </div>
      </div>

      <div className="min-w-0">
        {previewCaption && <div className="text-[10px] text-navy-300">{previewCaption}</div>}
      </div>
    </div>
  );
}

function createFocusPromise(staticId: number) {
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    const requestId = focarObjeto(String(staticId), finish);
    if (!requestId) {
      finish();
      return;
    }

    window.setTimeout(finish, 450);
  });
}

type EditMoldePageProps = {
  onBack?: () => void;
};

export function EditMoldePage({ onBack }: EditMoldePageProps) {
  const [source, setSource] = useState<InventorySource>('coleta');
  const [collectSnapshot, setCollectSnapshot] = useState<MoldeCollectResponse>(EMPTY_COLLECT);
  const [pageSnapshot, setPageSnapshot] = useState<EscanearPaginaResponse>(EMPTY_SCAN);
  const [collectWorksetIds, setCollectWorksetIds] = useState<number[]>([]);
  const [sheetCandidateId, setSheetCandidateId] = useState<number | null>(null);
  const [sizeSheetOpen, setSizeSheetOpen] = useState(false);
  const [activeMutation, setActiveMutation] = useState<{ id: number; label: string } | null>(null);
  const [readInfo, setReadInfo] = useState<LerPecaResponse | null>(null);
  const [message, setMessage] = useState<{ type: 'ok' | 'erro'; text: string } | null>(null);
  const [loadingSelection, setLoadingSelection] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMoldeMetadata, setSavingMoldeMetadata] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!message) return undefined;
    const timeoutId = window.setTimeout(() => setMessage(null), 3500);
    return () => window.clearTimeout(timeoutId);
  }, [message]);

  const inventory = useMemo<InventoryItem[]>(
    () => (source === 'coleta' ? collectSnapshot.objetos : pageSnapshot.candidatos).filter(isEditableStructuralItem),
    [collectSnapshot.objetos, pageSnapshot.candidatos, source]
  );
  const warnings = source === 'coleta' ? collectSnapshot.warnings : pageSnapshot.warnings;
  const moldeMetadata = source === 'coleta' ? collectSnapshot.molde : pageSnapshot.molde;

  const resumo = useMemo(() => {
    if (source === 'coleta') {
      return {
        mapeados: inventory.filter((item) => item.state === 'mapeado').length,
        pendentes: inventory.filter((item) => item.state === 'nao_mapeado').length,
        conflitos: inventory.filter((item) => item.state === 'conflito').length,
        paginaNome: collectSnapshot.pagina.nome || '--',
        documentoNome: collectSnapshot.documento.nome || 'Documento atual',
        documentoCaminho: collectSnapshot.documento.caminho || '',
        salvoNoDisco: collectSnapshot.documento.salvoNoDisco,
        tamanhoMolde: getMoldeMetadataLabel(collectSnapshot.molde),
        paginaCanonica: collectSnapshot.molde?.paginaCanonica || collectSnapshot.pagina.nome || '--',
        sincronizadoComPagina: Boolean(collectSnapshot.molde?.sincronizadoComPagina),
      };
    }

    return {
      mapeados: inventory.filter((item) => item.state === 'mapeado').length,
      pendentes: inventory.filter((item) => item.state === 'nao_mapeado').length,
      conflitos: inventory.filter((item) => item.state === 'conflito').length,
      paginaNome: pageSnapshot.pagina.nome || '--',
      documentoNome: pageSnapshot.pagina.documentoNome || 'Documento atual',
      documentoCaminho: pageSnapshot.pagina.documentoCaminho || '',
      salvoNoDisco: pageSnapshot.pagina.salvoNoDisco,
      tamanhoMolde: getMoldeMetadataLabel(pageSnapshot.molde),
      paginaCanonica: pageSnapshot.molde?.paginaCanonica || pageSnapshot.pagina.nome || '--',
      sincronizadoComPagina: Boolean(pageSnapshot.molde?.sincronizadoComPagina),
    };
  }, [collectSnapshot, inventory, pageSnapshot, source]);

  const sheetCandidate = useMemo(
    () => inventory.find((item) => item.staticId === sheetCandidateId) || null,
    [inventory, sheetCandidateId]
  );

  useEffect(() => {
    if (!sheetCandidateId) return;
    if (!inventory.some((item) => item.staticId === sheetCandidateId)) {
      setSheetCandidateId(null);
    }
  }, [inventory, sheetCandidateId]);

  const showMessage = useCallback((text: string, type: 'ok' | 'erro') => {
    setMessage({ text, type });
  }, []);

  const loadCollect = useCallback(
    async (options?: { nextFromId?: number; closeSheet?: boolean }) => {
      setLoadingSelection(true);
      try {
        const result = await coletarObjetos();
        const visibleItems = result.objetos.filter(isEditableStructuralItem);
        setCollectSnapshot(result);
        setCollectWorksetIds(result.objetos.map((item) => item.staticId));
        setSource('coleta');

        if (options?.closeSheet) {
          setSheetCandidateId(null);
        } else if (options?.nextFromId != null) {
          setSheetCandidateId(getNextPendingId(visibleItems, options.nextFromId));
        } else if (sheetCandidateId && visibleItems.some((item) => item.staticId === sheetCandidateId)) {
          setSheetCandidateId(sheetCandidateId);
        } else if (visibleItems.length === 1) {
          setSheetCandidateId(visibleItems[0].staticId);
        }
      } finally {
        setLoadingSelection(false);
      }
    },
    [sheetCandidateId]
  );

  const refreshCollectedWorkset = useCallback(
    async (options?: { nextFromId?: number; closeSheet?: boolean }) => {
      setLoadingSelection(true);
      try {
        const scanResult = await escanearPagina();
        setPageSnapshot(scanResult);
        const baseItems = collectSnapshot.objetos;
        const worksetIds = new Set(collectWorksetIds.length > 0 ? collectWorksetIds : baseItems.map((item) => item.staticId));
        const scannedSubset = scanResult.candidatos.filter((item) => worksetIds.has(item.staticId));
        const mergedObjects = mergeCollectedWorkset(baseItems, scannedSubset);
        const visibleItems = mergedObjects.filter(isEditableStructuralItem);

        setCollectSnapshot((current) => ({
          documento: {
            nome: scanResult.pagina.documentoNome || current.documento.nome,
            caminho: scanResult.pagina.documentoCaminho || current.documento.caminho,
            salvoNoDisco: scanResult.pagina.salvoNoDisco,
          },
          pagina: {
            nome: scanResult.pagina.nome || current.pagina.nome,
            indice: scanResult.pagina.indice || current.pagina.indice,
          },
          resumo: {
            total: mergedObjects.length,
            mapeados: mergedObjects.filter((item) => item.state === 'mapeado').length,
            pendentes: mergedObjects.filter((item) => item.state === 'nao_mapeado').length,
            placeholders: mergedObjects.filter((item) => item.state === 'placeholder').length,
            conflitos: mergedObjects.filter((item) => item.state === 'conflito').length,
          },
          molde: scanResult.molde ?? current.molde,
          objetos: mergedObjects,
          warnings: scanResult.warnings,
        }));

        setSource('coleta');

        if (options?.closeSheet) {
          setSheetCandidateId(null);
        } else if (options?.nextFromId != null) {
          setSheetCandidateId(getNextPendingId(visibleItems, options.nextFromId));
        } else if (sheetCandidateId && visibleItems.some((item) => item.staticId === sheetCandidateId)) {
          setSheetCandidateId(sheetCandidateId);
        }
      } finally {
        setLoadingSelection(false);
      }
    },
    [collectSnapshot.objetos, collectWorksetIds, sheetCandidateId]
  );

  const loadPage = useCallback(
    async (options?: { nextFromId?: number; closeSheet?: boolean }) => {
      setLoadingPage(true);
      try {
        const result = await escanearPagina();
        const visibleItems = result.candidatos.filter(isEditableStructuralItem);
        setPageSnapshot(result);
        setSource('pagina');

        if (options?.closeSheet) {
          setSheetCandidateId(null);
        } else if (options?.nextFromId != null) {
          setSheetCandidateId(getNextPendingId(visibleItems, options.nextFromId));
        } else if (sheetCandidateId && visibleItems.some((item) => item.staticId === sheetCandidateId)) {
          setSheetCandidateId(sheetCandidateId);
        }
      } finally {
        setLoadingPage(false);
      }
    },
    [sheetCandidateId]
  );

  const refreshActiveSource = useCallback(
    async (options?: { nextFromId?: number; closeSheet?: boolean }) => {
      if (source === 'coleta') {
        await refreshCollectedWorkset(options);
        return;
      }

      await loadPage(options);
    },
    [loadPage, refreshCollectedWorkset, source]
  );

  const handleFocus = useCallback(async (item: InventoryItem) => {
    await createFocusPromise(item.staticId);
  }, []);

  const handleReadActive = useCallback(async () => {
    try {
      const result = await lerPeca();
      setReadInfo(result);
    } catch (error: any) {
      showMessage(error.message || 'Falha ao ler shape ativo.', 'erro');
    }
  }, [showMessage]);

  const handleOpenConfig = useCallback(
    async (item: InventoryItem) => {
      setSheetCandidateId(item.staticId);
      await handleFocus(item);
    },
    [handleFocus]
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const result = await finalizarESalvarMoldeAtual();
      showMessage(
        result.salvo
          ? `Molde finalizado${result.paginaRenomeada ? `: ${result.paginaAnterior} -> ${result.paginaAtual}` : ''} e salvo em ${result.caminhoCompleto || result.nomeArquivo || 'arquivo atual'}.`
          : 'Nao foi possivel finalizar o molde atual.',
        result.salvo ? 'ok' : 'erro'
      );
      await refreshActiveSource();
    } catch (error: any) {
      showMessage(error.message || 'Falha ao finalizar e salvar o molde.', 'erro');
    } finally {
      setSaving(false);
    }
  }, [refreshActiveSource, showMessage]);

  const handleSaveMoldeMetadata = useCallback(
    async (draft: { familiaTamanho: string; tamanho: string }) => {
      setSavingMoldeMetadata(true);
      try {
        const saved = await salvarDadosMolde(draft);
        setCollectSnapshot((current) => ({ ...current, molde: saved }));
        setPageSnapshot((current) => ({ ...current, molde: saved }));
        showMessage(`Tamanho do molde salvo: ${saved.tamanho}.`, 'ok');
        setSizeSheetOpen(false);
      } catch (error: any) {
        showMessage(error.message || 'Falha ao salvar o tamanho do molde.', 'erro');
      } finally {
        setSavingMoldeMetadata(false);
      }
    },
    [showMessage]
  );

  const runMutation = useCallback(
    async (action: 'apply' | 'apply-next' | 'clear', state?: MoldeV3UiState) => {
      if (!sheetCandidate) return;

      setSubmitting(true);
      setActiveMutation({ id: sheetCandidate.staticId, label: action === 'clear' ? 'Limpando' : 'Aplicando' });
      try {
        showMessage(action === 'clear' ? 'Removendo mapeamento...' : 'Aplicando mapeamento...', 'ok');
        await handleFocus(sheetCandidate);

        if (action === 'clear') {
          await limparPeca();
          showMessage('Mapeamento removido do objeto atual.', 'ok');
          await refreshActiveSource({ closeSheet: true });
          return;
        }

        const payload = buildMoldePayload(state!);
        await marcarPeca(payload);
        showMessage(
          `Mapeamento aplicado: ${payload.peca}.${payload.parte || payload.placeholder || ''}`.replace(/\.$/, ''),
          'ok'
        );

        if (action === 'apply-next') {
          await refreshActiveSource({ nextFromId: sheetCandidate.staticId });
        } else {
          await refreshActiveSource({ closeSheet: true });
        }
      } catch (error: any) {
        showMessage(error.message || 'Falha ao atualizar o objeto.', 'erro');
      } finally {
        setActiveMutation(null);
        setSubmitting(false);
      }
    },
    [handleFocus, refreshActiveSource, sheetCandidate, showMessage]
  );

  return (
    <div className="relative h-full overflow-hidden bg-navy-950 text-white">
      <div className="flex h-full flex-col">
        <div className="border-b border-navy-700 bg-navy-950/90 px-3 py-2.5">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {onBack && (
                  <button
                    onClick={onBack}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-navy-700 bg-navy-900 text-navy-200 transition-colors hover:bg-navy-800 hover:text-white"
                    title="Voltar ao hub"
                  >
                    <ArrowLeft size={14} />
                  </button>
                )}
                <h2 className="text-sm font-semibold text-yellow-400">Editar Molde</h2>
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
              <button
                onClick={() => void loadCollect()}
                disabled={loadingSelection || submitting}
                className="rounded-lg border border-navy-600 bg-navy-800 px-2.5 py-1.5 text-[10px] font-semibold text-navy-100 transition-colors hover:bg-navy-700 disabled:opacity-50"
              >
                {loadingSelection ? 'Coletando...' : 'Coletar Objetos'}
              </button>
              <button
                onClick={() => void loadPage()}
                disabled={loadingPage || submitting}
                className="rounded-lg border border-navy-600 bg-navy-800 px-2.5 py-1.5 text-[10px] font-semibold text-navy-100 transition-colors hover:bg-navy-700 disabled:opacity-50"
              >
                {loadingPage ? 'Escaneando...' : 'Escanear Pagina'}
              </button>
              <button
                onClick={() => setSizeSheetOpen(true)}
                disabled={submitting || savingMoldeMetadata}
                className="inline-flex items-center gap-1.5 rounded-lg border border-navy-600 bg-navy-800 px-2.5 py-1.5 text-[10px] font-semibold text-navy-100 transition-colors hover:bg-navy-700 disabled:opacity-50"
              >
                <Ruler size={12} />
                Tamanho
              </button>
              <button
                onClick={() => void handleSave()}
                disabled={saving || submitting}
                className="rounded-lg bg-yellow-500 px-2.5 py-1.5 text-[10px] font-semibold text-navy-950 transition-colors hover:bg-yellow-400 disabled:opacity-50"
              >
                {saving ? 'Finalizando...' : 'Finalizar e salvar'}
              </button>
            </div>
          </div>

          {message && (
            <div
              className={`mt-2 rounded-lg border px-2.5 py-2 text-[11px] ${
                message.type === 'ok'
                  ? 'border-green-700/30 bg-green-900/10 text-green-200'
                  : 'border-red-700/30 bg-red-900/10 text-red-200'
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-5">
            <div className="rounded-xl border border-navy-700 bg-navy-900/70 px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-[0.16em] text-navy-400">Pagina</div>
              <div className="mt-0.5 text-sm font-semibold text-white">{resumo.paginaNome}</div>
            </div>
            <div className="rounded-xl border border-cyan-700/30 bg-cyan-900/10 px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-[0.16em] text-cyan-300">Tamanho</div>
              <div className="mt-0.5 text-sm font-semibold text-white">{resumo.tamanhoMolde}</div>
              <div className="mt-0.5 text-[10px] text-cyan-100/80">{getMoldeSyncLabel(moldeMetadata)}</div>
            </div>
            <div className="rounded-xl border border-green-700/30 bg-green-900/10 px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-[0.16em] text-green-300">Mapeados</div>
              <div className="mt-0.5 text-sm font-semibold text-white">{String(resumo.mapeados).padStart(2, '0')}</div>
            </div>
            <div className="rounded-xl border border-yellow-700/30 bg-yellow-900/10 px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-[0.16em] text-yellow-300">Pendentes</div>
              <div className="mt-0.5 text-sm font-semibold text-white">{String(resumo.pendentes).padStart(2, '0')}</div>
            </div>
            <div className="rounded-xl border border-red-700/30 bg-red-900/10 px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-[0.16em] text-red-300">Conflitos</div>
              <div className="mt-0.5 text-sm font-semibold text-white">{String(resumo.conflitos).padStart(2, '0')}</div>
            </div>
          </div>

          <div className="mt-2 rounded-xl border border-navy-700 bg-navy-900/50 px-2.5 py-2 text-[11px] text-navy-300">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-navy-100">{resumo.documentoNome || 'Documento atual'}</div>
                <div className="truncate text-[10px] text-navy-400">
                  {resumo.documentoCaminho || 'Sem caminho detectado'}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="rounded-full border border-cyan-700/30 bg-cyan-900/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-cyan-200">
                  {source === 'coleta' ? 'Selecao' : 'Pagina'}
                </span>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                    moldeMetadata?.tamanho
                      ? 'border-cyan-700/30 bg-cyan-900/10 text-cyan-200'
                      : 'border-yellow-700/30 bg-yellow-900/10 text-yellow-200'
                  }`}
                >
                  Tamanho {moldeMetadata?.tamanho || 'pendente'}
                </span>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                    resumo.sincronizadoComPagina
                      ? 'border-green-700/30 bg-green-900/10 text-green-200'
                      : 'border-yellow-700/30 bg-yellow-900/10 text-yellow-200'
                  }`}
                >
                  {resumo.sincronizadoComPagina ? `Pagina ${resumo.paginaCanonica}` : `Pagina atual ${resumo.paginaNome}`}
                </span>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                    resumo.salvoNoDisco
                      ? 'border-green-700/30 bg-green-900/10 text-green-200'
                      : 'border-yellow-700/30 bg-yellow-900/10 text-yellow-200'
                  }`}
                >
                  {resumo.salvoNoDisco ? 'Salvo' : 'Nao salvo'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          <div className="rounded-xl border border-navy-700 bg-navy-900/50 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Layers3 size={14} className="text-yellow-400" />
                  <h3 className="text-[13px] font-semibold text-yellow-400">Objetos</h3>
                  <span className="rounded-full border border-navy-600 bg-navy-800 px-2 py-0.5 text-[9px] font-semibold text-navy-300">
                    {inventory.length}
                  </span>
                </div>
              </div>
              <button
                onClick={() => void handleReadActive()}
                className="rounded-lg border border-navy-600 bg-navy-800 px-2.5 py-1.5 text-[10px] font-semibold text-navy-100 transition-colors hover:bg-navy-700"
              >
                Ler ativo
              </button>
            </div>

            {readInfo && (
              <div className="mt-2 rounded-lg border border-navy-700 bg-navy-800/70 px-2.5 py-2 text-[11px] text-navy-200">
                {buildSelectionInfo(readInfo)}
              </div>
            )}

            {warnings.length > 0 && (
              <div className="mt-2 space-y-2">
                {warnings.map((warning, index) => (
                  <div
                    key={`${warning}-${index}`}
                    className="rounded-lg border border-yellow-700/30 bg-yellow-900/10 px-2.5 py-2 text-[11px] text-yellow-200"
                  >
                    {warning}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 space-y-2.5">
              {inventory.length === 0 && (
                <div className="rounded-xl border border-dashed border-navy-700 bg-navy-950/40 px-3 py-8 text-center text-xs text-navy-400">
                  {source === 'coleta' ? 'Selecione objetos e clique em Coletar Objetos.' : 'Sem objetos retornados.'}
                </div>
              )}

              {inventory.map((item) => {
                const stateMeta =
                  activeMutation?.id === item.staticId
                    ? {
                        label: activeMutation.label,
                        className: 'border-cyan-700/30 bg-cyan-900/10 text-cyan-200',
                        icon: CircleDashed,
                      }
                    : getStateMeta(item.state);
                const StateIcon = stateMeta.icon;
                const isActive = sheetCandidateId === item.staticId;
                const primaryLabel = getItemPrimaryLabel(item);
                const mappingLabel = getItemMappingLabel(item);

                return (
                  <div
                    key={item.staticId}
                    className={`rounded-xl border px-2.5 py-2.5 transition-colors ${
                      isActive ? 'border-yellow-500 bg-navy-800/80' : 'border-navy-700 bg-navy-900/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <PreviewShapeCard item={item} />

                      <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="truncate text-[13px] font-semibold leading-5 text-white">{primaryLabel}</div>
                            <span
                              className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${stateMeta.className}`}
                            >
                              {stateMeta.label}
                            </span>
                          </div>

                          <div className="mt-0.5 text-[10px] text-navy-400">
                            {item.layerName || 'Sem camada'} · #{item.staticId}
                            {'parentGroupName' in item && item.parentGroupName ? ` - Grupo ${item.parentGroupName}` : ''}
                          </div>

                          {mappingLabel && (
                            <div className="mt-1 text-[10px] font-medium text-yellow-300">{mappingLabel}</div>
                          )}

                          {item.problems && item.problems.length > 0 && (
                            <div className="mt-1.5 space-y-1">
                              {item.problems.map((problem, index) => (
                                <div key={`${item.staticId}-${index}`} className="text-[10px] text-red-200">
                                  {problem}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex shrink-0 gap-1.5">
                          <button
                            onClick={() => void handleFocus(item)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-navy-600 bg-navy-800 text-navy-100 transition-colors hover:bg-navy-700"
                            title="Focar no Corel"
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            onClick={() => void handleOpenConfig(item)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-yellow-500 text-navy-950 transition-colors hover:bg-yellow-400"
                            title="Configurar"
                          >
                            <Settings2 size={15} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <ConfigSheet
        candidate={sheetCandidate}
        open={Boolean(sheetCandidate)}
        busy={submitting}
        canGoNext={sheetCandidate ? getNextPendingId(inventory, sheetCandidate.staticId) != null : false}
        readInfo={readInfo}
        onClose={() => setSheetCandidateId(null)}
        onFocus={handleFocus}
        onReadActive={handleReadActive}
        onApply={async (state) => {
          await runMutation('apply', state);
        }}
        onApplyAndNext={async (state) => {
          await runMutation('apply-next', state);
        }}
        onClear={async () => {
          await runMutation('clear');
        }}
      />

      <SizeSheet
        open={sizeSheetOpen}
        busy={savingMoldeMetadata}
        molde={moldeMetadata}
        onClose={() => setSizeSheetOpen(false)}
        onSave={handleSaveMoldeMetadata}
      />
    </div>
  );
}
